import { useEffect } from 'react';

import { PAGE_ROOT_ID } from '#src/shared/pageRoot';

/**
 * Marks the page root `inert` while `isActive`, so a full-bleed overlay portaled out of it cannot be
 * tabbed behind.
 *
 * `aria-modal` keeps assistive technology out of the background but does nothing for a sighted
 * keyboard user, and `tabindex="-1"` would have to be applied to every focusable node and restored
 * exactly. `inert` is one attribute on one element, and the browser removes the whole subtree from
 * the tab order, from hit testing and from the accessibility tree at once.
 *
 * Only works for an overlay rendered **outside** the root — inerting an ancestor would inert the
 * overlay too.
 *
 * The previous value is restored rather than cleared, so nesting (the share sheet layering over the
 * success screen) cannot leave the page permanently inert.
 */
export function useInertPageRoot(isActive: boolean): void {
	useEffect(() => {
		if (!isActive) return undefined;

		const root = document.getElementById(PAGE_ROOT_ID);
		if (!root) return undefined;

		// The attribute, not the `inert` property: both work in a browser, but jsdom implements neither
		// the property nor its reflection — so a property-based version would be untestable here and
		// silently inert nothing under test.
		const wasInert = root.hasAttribute('inert');
		root.setAttribute('inert', '');

		return () => {
			if (!wasInert) root.removeAttribute('inert');
		};
	}, [isActive]);
}
